import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";

const FocusableScrollContainer = ({
  height,
  component = Box,
  children,
  sx,
  onScroll,
  ...props
}) => {
  const scrollContainerRef = React.useRef(null);

  // Attach scroll listener if onScroll prop is provided
  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !onScroll) return;

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [onScroll]);

  const onKeyDown = React.useCallback((e) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const step = 40;
    const page = el.clientHeight - 20;
    switch (e.key) {
      case "ArrowDown":
        el.scrollBy({ top: step, behavior: "smooth" });
        e.preventDefault();
        break;
      case "ArrowUp":
        el.scrollBy({ top: -step, behavior: "smooth" });
        e.preventDefault();
        break;
      case "PageDown":
        el.scrollBy({ top: page, behavior: "smooth" });
        e.preventDefault();
        break;
      case "PageUp":
        el.scrollBy({ top: -page, behavior: "smooth" });
        e.preventDefault();
        break;
      case "Home":
        el.scrollTo({ top: 0, behavior: "smooth" });
        e.preventDefault();
        break;
      case "End":
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        e.preventDefault();
        break;
      default:
        break;
    }
  }, []);

  return (
    <Box
      ref={scrollContainerRef}
      component={component}
      tabIndex={0}
      onKeyDown={onKeyDown}
      {...props}
      sx={{
        outline: "none",
        "&:focus-visible": {
          boxShadow: "0 0 0 2px #403C6F",
        },
        overflowY: "auto",
        maxHeight: height,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default FocusableScrollContainer;

FocusableScrollContainer.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onScroll: PropTypes.func,
};
